import React, { useState, useEffect } from 'react';
import { Clock, Shield, Truck } from 'lucide-react';

// Shipping countdown timer - ships Mon-Wed at 6pm Central
export const ShippingCountdown = ({ className = '' }) => {
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [nextShipDay, setNextShipDay] = useState('');
  const [canShipToday, setCanShipToday] = useState(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      // Get current time in Central Time (approximate)
      const now = new Date();
      const centralOffset = -6; // CST
      const utcHours = now.getUTCHours();
      const centralHours = (utcHours + centralOffset + 24) % 24;
      
      const dayOfWeek = now.getUTCDay(); // 0 = Sunday, 6 = Saturday
      const centralDay = centralHours < Math.abs(centralOffset) && utcHours > 12 
        ? (dayOfWeek + 1) % 7 
        : dayOfWeek;
      
      // Shipping days: Mon(1), Tue(2), Wed(3) at 6pm (18:00)
      const shipHour = 18; // 6 PM
      
      let daysUntilShip = 0;
      let shipDayName = '';
      
      // Determine next ship day (Mon-Wed only)
      if (centralDay >= 1 && centralDay <= 3) {
        // It's Mon, Tue, or Wed
        if (centralHours < shipHour) {
          // Can still ship today
          daysUntilShip = 0;
          shipDayName = 'today';
          setCanShipToday(true);
        } else {
          // Past 6pm
          setCanShipToday(false);
          if (centralDay === 3) {
            // Wednesday after 6pm -> next Monday
            daysUntilShip = 5;
            shipDayName = 'Monday';
          } else {
            daysUntilShip = 1;
            const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
            shipDayName = days[(centralDay + 1) % 7];
          }
        }
      } else if (centralDay === 4) {
        // Thursday -> Monday
        daysUntilShip = 4;
        shipDayName = 'Monday';
        setCanShipToday(false);
      } else if (centralDay === 5) {
        // Friday -> Monday
        daysUntilShip = 3;
        shipDayName = 'Monday';
        setCanShipToday(false);
      } else if (centralDay === 6) {
        // Saturday -> Monday
        daysUntilShip = 2;
        shipDayName = 'Monday';
        setCanShipToday(false);
      } else {
        // Sunday -> Monday
        daysUntilShip = 1;
        shipDayName = 'Monday';
        setCanShipToday(false);
      }
      
      setNextShipDay(shipDayName);
      
      // Calculate time until 6pm cutoff (only relevant if shipping today)
      if (daysUntilShip === 0) {
        const minutesUntilCutoff = (shipHour - centralHours) * 60 - now.getMinutes();
        const hours = Math.floor(minutesUntilCutoff / 60);
        const minutes = minutesUntilCutoff % 60;
        const seconds = 60 - now.getSeconds();
        
        setTimeLeft({
          hours: Math.max(0, hours),
          minutes: Math.max(0, minutes),
          seconds: Math.max(0, seconds % 60)
        });
      } else {
        // Show time until next shipping day at 6pm
        const targetDate = new Date(now);
        targetDate.setDate(targetDate.getDate() + daysUntilShip);
        targetDate.setHours(shipHour + Math.abs(centralOffset), 0, 0, 0);
        
        const diff = targetDate - now;
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        
        setTimeLeft({ hours, minutes, seconds });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className={`bg-gradient-to-r from-primary/10 to-green-50 border border-primary/20 rounded-xl p-4 ${className}`} data-testid="shipping-countdown">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
          <Truck className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1">
          {canShipToday ? (
            <>
              <p className="text-sm font-semibold text-primary">Order within</p>
              <div className="flex items-center gap-1 text-lg font-bold">
                <span className="bg-primary text-white px-2 py-0.5 rounded">{String(timeLeft.hours).padStart(2, '0')}</span>
                <span>:</span>
                <span className="bg-primary text-white px-2 py-0.5 rounded">{String(timeLeft.minutes).padStart(2, '0')}</span>
                <span>:</span>
                <span className="bg-primary text-white px-2 py-0.5 rounded">{String(timeLeft.seconds).padStart(2, '0')}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">to ship TODAY at 6 PM CT</p>
            </>
          ) : (
            <>
              <p className="text-sm font-semibold text-foreground">Next shipment: <span className="text-primary">{nextShipDay}</span></p>
              <p className="text-xs text-muted-foreground">Orders ship Mon-Wed at 6 PM CT</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Live Arrival Guarantee Badge
export const LiveArrivalBadge = ({ variant = 'default', className = '' }) => {
  if (variant === 'compact') {
    return (
      <div className={`inline-flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 px-3 py-1.5 rounded-full text-sm font-medium ${className}`} data-testid="live-arrival-badge">
        <Shield className="w-4 h-4" />
        100% Live Arrival Guarantee
      </div>
    );
  }
  
  return (
    <div className={`bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 ${className}`} data-testid="live-arrival-badge">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
          <Shield className="w-5 h-5 text-green-600" />
        </div>
        <div>
          <p className="font-semibold text-green-800">100% Live Arrival Guarantee</p>
          <p className="text-sm text-green-600">
            Plants arrive alive & healthy or we replace them - no questions asked
          </p>
        </div>
      </div>
    </div>
  );
};

// Low Stock Badge
export const LowStockBadge = ({ quantity, threshold = 5 }) => {
  if (quantity <= 0) {
    return (
      <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs font-medium" data-testid="out-of-stock-badge">
        Out of Stock
      </span>
    );
  }
  
  if (quantity <= threshold) {
    return (
      <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 px-2 py-1 rounded-full text-xs font-medium animate-pulse" data-testid="low-stock-badge">
        <Clock className="w-3 h-3" />
        Only {quantity} left!
      </span>
    );
  }
  
  return null;
};

// Estimated Delivery Calculator
export const getEstimatedDelivery = () => {
  const now = new Date();
  const centralOffset = -6;
  const utcHours = now.getUTCHours();
  const centralHours = (utcHours + centralOffset + 24) % 24;
  const dayOfWeek = now.getDay();
  
  const shipHour = 18;
  let daysUntilShip = 0;
  
  // Calculate days until ship (Mon-Wed only)
  if (dayOfWeek >= 1 && dayOfWeek <= 3) {
    // Mon, Tue, Wed
    if (centralHours < shipHour) {
      daysUntilShip = 0;
    } else {
      // After 6pm
      if (dayOfWeek === 3) {
        daysUntilShip = 5; // Wed after 6pm -> Monday
      } else {
        daysUntilShip = 1;
      }
    }
  } else if (dayOfWeek === 4) {
    daysUntilShip = 4; // Thu -> Monday
  } else if (dayOfWeek === 5) {
    daysUntilShip = 3; // Fri -> Monday
  } else if (dayOfWeek === 6) {
    daysUntilShip = 2; // Sat -> Monday
  } else {
    daysUntilShip = 1; // Sun -> Monday
  }
  
  // USPS Priority is 1-3 days, estimate 2 days for delivery
  const deliveryDays = 2;
  const totalDays = daysUntilShip + deliveryDays;
  
  const deliveryDate = new Date(now);
  deliveryDate.setDate(deliveryDate.getDate() + totalDays);
  
  // Skip weekends for delivery estimate
  while (deliveryDate.getDay() === 0 || deliveryDate.getDay() === 6) {
    deliveryDate.setDate(deliveryDate.getDate() + 1);
  }
  
  const options = { weekday: 'short', month: 'short', day: 'numeric' };
  return deliveryDate.toLocaleDateString('en-US', options);
};

export const EstimatedDelivery = ({ className = '' }) => {
  const [deliveryDate, setDeliveryDate] = useState('');
  
  useEffect(() => {
    setDeliveryDate(getEstimatedDelivery());
  }, []);
  
  return (
    <div className={`flex items-center gap-2 text-sm ${className}`} data-testid="estimated-delivery">
      <Truck className="w-4 h-4 text-primary" />
      <span className="text-muted-foreground">Estimated delivery:</span>
      <span className="font-semibold text-primary">{deliveryDate}</span>
    </div>
  );
};
